import { useState, useEffect, useRef, useCallback } from "react";
import { ShieldCheck, KeyRound } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { extractError } from "../lib/errors";

/**
 * TwoFactorChallenge — muestra el formulario del segundo factor tras el login.
 *
 * Props:
 *   challengeToken  : string   — token opaco recibido del backend (5 min de vida)
 *   expiresAt       : string   — ISO datetime de expiración
 *   onSuccess       : ({token, user}) => void  — callback al verificar OK
 *   onExpired       : () => void               — callback al agotar el tiempo
 */
const TwoFactorChallenge = ({ challengeToken, expiresAt, onSuccess, onExpired }) => {
  const { t } = useI18n();
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(() => {
    const diff = Math.floor((new Date(expiresAt) - Date.now()) / 1000);
    return Math.max(0, diff);
  });

  const codeInputRef = useRef(null);

  // Auto-focus on mount
  useEffect(() => {
    codeInputRef.current?.focus();
  }, [useRecovery]);

  // Countdown timer
  useEffect(() => {
    if (secondsLeft <= 0) {
      onExpired?.();
      return;
    }
    const id = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(id);
          onExpired?.();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formatCountdown = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const handleVerify = useCallback(async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!useRecovery) {
        if (!code || code.length !== 6) {
          toast.error(t("auth.2fa.challenge.code_required"));
          return;
        }
        const res = await axios.post(`${API}/auth/login/verify-2fa`, {
          challenge_token: challengeToken,
          code: code.trim(),
        });
        onSuccess(res.data);
      } else {
        const cleanRecovery = recoveryCode.trim().toUpperCase();
        if (!cleanRecovery) {
          toast.error(t("auth.2fa.challenge.recovery_required"));
          return;
        }
        const res = await axios.post(`${API}/auth/login/recovery`, {
          challenge_token: challengeToken,
          recovery_code: cleanRecovery,
        });
        if (res.data.recovery_remaining !== undefined && res.data.recovery_remaining <= 2) {
          toast.warning(
            t("auth.2fa.challenge.recovery_low").replace("{n}", res.data.recovery_remaining)
          );
        }
        onSuccess(res.data);
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        toast.error(t("auth.2fa.challenge.too_many_attempts"));
      } else {
        toast.error(extractError(err, t("auth.2fa.challenge.invalid_code")));
      }
    } finally {
      setLoading(false);
    }
  }, [useRecovery, code, recoveryCode, challengeToken, onSuccess, t]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-semibold">{t("auth.2fa.challenge.title")}</h2>
      </div>

      {/* Countdown */}
      <div
        className={`text-sm font-medium ${
          secondsLeft < 60 ? "text-destructive" : "text-muted-foreground"
        }`}
      >
        {t("auth.2fa.challenge.expires_in").replace("{seconds}", formatCountdown(secondsLeft))}
      </div>

      <form onSubmit={handleVerify} className="space-y-4">
        {!useRecovery ? (
          <div className="space-y-2">
            <Label htmlFor="totp-challenge">{t("auth.2fa.challenge.code_label")}</Label>
            <Input
              id="totp-challenge"
              ref={codeInputRef}
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              autoComplete="one-time-code"
              disabled={loading}
            />
          </div>
        ) : (
          <div className="space-y-2">
            <Label htmlFor="recovery-challenge">
              <span className="flex items-center gap-1">
                <KeyRound className="w-3 h-3" />
                {t("auth.2fa.challenge.recovery_label")}
              </span>
            </Label>
            <Input
              id="recovery-challenge"
              ref={codeInputRef}
              type="text"
              placeholder="XXXXXXXX-XXXXXXXX"
              value={recoveryCode}
              onChange={(e) =>
                setRecoveryCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ""))
              }
              maxLength={17}
              disabled={loading}
            />
          </div>
        )}

        <Button type="submit" className="w-full" disabled={loading || secondsLeft === 0}>
          {loading ? t("common.loading") : t("auth.2fa.challenge.verify")}
        </Button>
      </form>

      <button
        type="button"
        className="text-xs text-muted-foreground hover:text-primary underline underline-offset-2 transition-colors"
        onClick={() => {
          setUseRecovery((v) => !v);
          setCode("");
          setRecoveryCode("");
        }}
      >
        {useRecovery
          ? t("auth.2fa.challenge.use_totp")
          : t("auth.2fa.challenge.use_recovery")}
      </button>
    </div>
  );
};

export default TwoFactorChallenge;
